// Shared burst selection for two people (v0.7.2). Each phone keeps the frames around the
// shutter instant; choosing the steadiest frame on each phone independently could pair poses
// up to a window apart. Instead the phones send only metadata (time on the host clock +
// motion score) and the host picks ONE pair: close in time first, then steady. Images never
// leave the phones for this.

export interface BurstCand {
  /** candidate index on its phone */
  k: number;
  /** capture time on the HOST clock (ms) */
  tHost: number;
  /** 0 still … 1 moving fast */
  motion: number;
}

/** Above this skew a pair counts as "not simultaneous" (≈ two frames at 30 fps). */
export const MAX_SKEW_MS = 70;

export interface PairChoice { a: number; b: number; skew: number; withinSkew: boolean }

/**
 * Picks the pair (candidate of A, candidate of B):
 *  1. among pairs within MAX_SKEW_MS: least combined motion, ties → smaller skew;
 *  2. none within the limit (slow cameras, clock error): the pair closest in time.
 * null when one side has no candidates (each phone then keeps its own choice).
 */
export function choosePair(a: BurstCand[], b: BurstCand[], maxSkew = MAX_SKEW_MS): PairChoice | null {
  if (!a.length || !b.length) return null;
  let best: PairChoice | null = null, bestCost = Infinity;
  let closest: PairChoice | null = null;
  for (const x of a) for (const y of b) {
    const skew = Math.abs(x.tHost - y.tHost);
    if (!closest || skew < closest.skew) closest = { a: x.k, b: y.k, skew, withinSkew: skew <= maxSkew };
    if (skew > maxSkew) continue;
    const cost = (x.motion + y.motion) * 100 + skew / 33;
    if (cost < bestCost - 1e-9) { bestCost = cost; best = { a: x.k, b: y.k, skew, withinSkew: true }; }
  }
  return best ?? closest;
}

/** Solo shot (or no partner answer): the steadiest own candidate, ties → closest to `atHost`. */
export function chooseOwn(c: BurstCand[], atHost: number): number | null {
  if (!c.length) return null;
  return c.reduce((best, x) => (x.motion < best.motion - 0.05 || (Math.abs(x.motion - best.motion) <= 0.05 && Math.abs(x.tHost - atHost) < Math.abs(best.tHost - atHost)) ? x : best)).k;
}
